import { Icon, Tooltip } from "@material-ui/core";
import React from "react";
import styled, { createGlobalStyle, css } from "styled-components";

import { t } from "js/i18n";
import { IconMenu } from "js/react/components/UiComponents";
import { ThumbnailCard, thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";

export const TEAM_RESOURCE_DESCRIPTION_TOOLTIP_CLASS = "team-resource-description-tooltip";

export const TeamResourceTooltipStyles = createGlobalStyle`
    .${TEAM_RESOURCE_DESCRIPTION_TOOLTIP_CLASS}.MuiTooltip-tooltip {
        color: #444;
        font-size: 12px;
        line-height: 125%;
    }
`;

const teamResourceCardSelectedStyles = css<{ $clickable?: boolean }>`
    border: none;
    background: #D5E6ED;

    :hover {
        cursor: ${props => props.$clickable ? "pointer" : "default"};
        background: #D5E6ED;
        // Reset the base ThumbnailCard's selected-hover drop shadow so selected cards match
        // the presentation library grid (no card-level shadow on hover).
        box-shadow: none;
    }
`;

const teamResourceCardDefaultStyles = css<{ $clickable?: boolean }>`
    border: none;
    background: #FFF;

    :hover {
        cursor: ${props => props.$clickable ? "pointer" : "default"};
        background: #F8FAFB;
        box-shadow: inset 0 0 0 1px #DDE3E8;
    }
`;

export const TeamResourceThumbnailCard = styled(ThumbnailCard)<{ selected?: boolean; $contentGap?: number; $clickable?: boolean }>`
    &&& {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: ${props => props.$contentGap ?? 12}px;
        padding: 15px 15px 12px;
        border-radius: 4px;
        overflow: hidden;

        ${props => props.selected ? teamResourceCardSelectedStyles : teamResourceCardDefaultStyles}

        > .divider {
            display: none;
        }
    }
`;

export const TeamResourceThumbnailActions = styled.div`
    position: absolute;
    right: 6px;
    bottom: 6px;
    z-index: 1001;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const TeamResourceIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
        background-color: #111827E6;
        border: 1px solid rgba(255, 255, 255, 0.28);
        border-radius: 50%;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
        color: white;
        width: 28px;
        height: 28px;
        transition: background-color 0.15s ease, transform 0.15s ease, border-color 0.15s ease;

        &:hover {
            background-color: #111827;
            border-color: rgba(255, 255, 255, 0.42);
            transform: scale(1.04);
        }

        .MuiIcon-root {
            color: white;
        }
    }
`;

export const TeamResourceThumbnailImageWrap = styled.div`
    position: relative;
    width: 100%;
    border-radius: 1px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    .thumbnail,
    img {
        border-radius: 1px;
    }

    ${TeamResourceThumbnailCard}:hover & {
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.16);
    }
`;

export const TeamResourceHiddenIndicator = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiIcon-root {
        color: white;
        font-size: 20px;
    }
`;

const UsageTooltip = styled(Tooltip)`
`;

const UsageTooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 220px;
    padding: 4px;
`;

const UsageTooltipText = styled.div`
    color: white;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 11px;
    font-style: normal;
    line-height: 125%;
`;

const UsageTooltipTitle = styled(UsageTooltipText)`
    font-weight: 600;
`;

const UsageTooltipDescription = styled(UsageTooltipText)`
    font-weight: 400;
`;

const UsageTooltipStrong = styled.span`
    font-weight: 700;
`;

const UsageBadge = styled.div`
    background: #FDE5D5;
    border-radius: 999px;
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 3px;
    width: max-content;
    min-width: max-content;
    height: 20px;
    padding: 0 6px 0 4px;
    color: #9B321C;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;

    .MuiIcon-root {
        color: #FF4500;
        margin: 0;
        padding: 0;
        font-size: 16px;
    }

    span {
        flex: 0 0 auto;
    }
`;

export const TeamResourceBadgeCluster = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    min-width: 0;
`;

const UsefulnessButton = styled.button<{ $active: boolean; $hasCount: boolean; $variant: "overlay" | "inline"; $visible: boolean }>`
    appearance: none;
    border: none;
    background: ${props => props.$variant === "overlay" ? "#111827E6" : "#EEF3F6"};
    border-radius: 999px;
    box-sizing: border-box;
    box-shadow: ${props => props.$variant === "overlay" ? "0 1px 2px rgba(0, 0, 0, 0.18)" : "none"};
    color: ${props => props.$variant === "overlay" ? "white" : props.$active ? "#6F7780" : "#9AA2AA"};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Source Sans Pro", sans-serif;
    margin: 0;
    opacity: ${props => props.$visible ? 1 : 0};
    pointer-events: ${props => props.$visible ? "auto" : "none"};
    transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;

    ${props => props.$variant === "inline" ? css`
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        gap: 3px;
        width: ${props.$hasCount ? "max-content" : "20px"};
        min-width: ${props.$hasCount ? "max-content" : "20px"};
        height: 20px;
        padding: ${props.$hasCount ? "0 6px 0 4px" : "0"};
        font-size: 11px;
        font-weight: 600;
        line-height: 16px;
        white-space: nowrap;
    ` : css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        flex: 0 0 auto;
        font-size: 11px;
        font-weight: 600;
        height: 28px;
        line-height: 16px;
        min-width: 28px;
        padding: ${props.$hasCount ? "0 8px" : "0"};
        white-space: nowrap;
    `}

    .MuiIcon-root {
        color: ${props => props.$variant === "overlay" ? "inherit" : props.$active ? "#11A9E2" : "#9AA2AA"};
        font-size: ${props => props.$variant === "overlay" ? 13 : 14}px;
        margin: 0;
        padding: 0;
    }

    span {
        color: ${props => props.$variant === "overlay" ? "inherit" : "#9AA2AA"};
        flex: 0 0 auto;
    }

    :hover {
        background-color: ${props => props.$variant === "overlay" ? "#111827" : "#E6EEF3"};
        color: ${props => props.$variant === "overlay" ? "white" : "#6F7780"};

        .MuiIcon-root {
            color: ${props => props.$variant === "overlay" ? "inherit" : props.$active ? "#11A9E2" : "#6F7780"};
        }
    }

    :focus-visible {
        opacity: 1;
        pointer-events: auto;
        outline: 2px solid #11A9E2;
        outline-offset: 2px;
    }
`;

export function TeamResourceUsefulnessButton({
    active,
    count,
    visible,
    activeLabel,
    inactiveLabel,
    onClick,
    variant = "overlay",
}: {
    active: boolean;
    count: number;
    visible: boolean;
    activeLabel: string;
    inactiveLabel: string;
    onClick: () => void;
    variant?: "overlay" | "inline";
}) {
    const label = active ? activeLabel : inactiveLabel;

    return (
        <UsefulnessButton
            type="button"
            $active={active}
            $hasCount={count > 0}
            $variant={variant}
            $visible={visible}
            aria-pressed={active}
            aria-label={label}
            tabIndex={visible ? 0 : -1}
            onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onMouseUp={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onDoubleClick={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onClick();
            }}
        >
            <Icon fontSize="small">thumb_up_off_alt</Icon>
            {count > 0 && <span>{count}</span>}
        </UsefulnessButton>
    );
}

export function TeamResourceUsageBadge({
    count,
    hidden,
    showTitle = false,
    title,
    description,
}: {
    count: number;
    hidden?: boolean;
    showTitle?: boolean;
    title: React.ReactNode;
    description?: React.ReactNode;
}) {
    const badge = (
        <UsageBadge>
            <Icon fontSize="small">link</Icon>
            <span>{count}</span>
        </UsageBadge>
    );

    if (hidden) {
        return null;
    }

    return (
        <UsageTooltip
            title={(
                <UsageTooltipContent>
                    {showTitle && <UsageTooltipTitle>{title}</UsageTooltipTitle>}
                    {description && (
                        <UsageTooltipDescription>
                            {description}
                        </UsageTooltipDescription>
                    )}
                    <UsageTooltipDescription>
                        {t("Used in")}{" "}
                        <UsageTooltipStrong>
                            {count} {count === 1 ? t("Presentation") : t("Presentations")}
                        </UsageTooltipStrong>
                    </UsageTooltipDescription>
                </UsageTooltipContent>
            )}
            placement="top"
            enterDelay={500}
            enterNextDelay={500}
            arrow
        >
            {badge}
        </UsageTooltip>
    );
}
